// ============================================================
//  MASAMADREAM — App Entry Point
//  Fase 5: onboarding, moods, web share, abandon process
// ============================================================

import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Store }      from './store.js';
import { PROCESSES }  from './data.js';
import { showScreen } from './ui.js';
import { toast }      from './toast.js';
import { startCountdown, stopCountdown } from './countdown.js';
import { renderActivities }             from './activities.js';
import { renderNotifications, scheduleNotifications, _scheduleAllPending, clearNotificationsForProcess } from './notifications.js';
import { createDial, startPhraseRotation, stopPhraseRotation } from './dial.js';
import { initActivityModal }           from './activity-modal.js';
import { initPwaPrompt }               from './pwa.js';
import { renderHistory }               from './history.js';
import { shouldOnboard, initOnboarding } from './onboarding.js';
import { refreshMood }                 from './mascot.js';
import { checkAchievements }           from './achievements.js';
import { initSettings, renderSettings } from './settings.js';

// ─── Service Worker ────────────────────────────────────────
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(new URL('../sw.js', import.meta.url), { type: 'classic' })
      .then(() => console.log('[MM] SW registered ✓'))
      .catch(err => console.warn('[MM] SW failed:', err));
  });
}

// ─── Boot ──────────────────────────────────────────────────
document.addEventListener('DOMContentLoaded', () => {
  initApp();
});

let _store = null;

function initApp() {
  _store = new Store();
  initActivityModal();
  initPwaPrompt();
  initSettings(_store, {
    onBack: () => {
      const state = _store.load();
      if (state.processes?.length) {
        showScreen('dashboard');
      } else {
        showScreen('home');
        updateGreeting(_store);
      }
    },
    onReset: () => {
      showScreen('onboarding');
      initOnboarding(() => _bootHome(_store));
    },
    onNameSaved: () => updateGreeting(_store),
    onStartDateChanged: () => {
      const updated = _store.load();
      const viewing = _store.getViewingProcess();
      if (viewing) {
        stopCountdown();
        stopPhraseRotation();
        openDashboard(viewing, _store);
      }
    },
  });
  wireSettingsButton(_store);
  wireHomeButtons(_store);
  wireDashboardButtons(_store);
  wireHistoryScreen(_store);
  wireAbandonButton(_store);

  // Restore scheduled notifications from storage
  _scheduleAllPending();

  if (shouldOnboard()) {
    // Show onboarding first, then home
    showScreen('onboarding');
    initOnboarding(() => {
      _bootHome(_store);
    });
  } else {
    _bootHome(_store);
  }

  requestAnimationFrame(() => {
    document.getElementById('mm-loading')?.classList.add('is-hidden');
  });
}

function _bootHome(store) {
  const state = store.load();
  updateGreeting(store);
  showScreen('home');
  renderActiveProcessList(store);
  const firstProc = state.processes?.[0] ?? null;
  refreshMood(firstProc, store);
  if (state.history?.length) {
    document.getElementById('mm-btn-view-history')?.classList.remove('mm-hidden');
  }
}

// ─── Greeting ──────────────────────────────────────────────
function updateGreeting(store) {
  const name = store.getStarterName();
  const el   = document.getElementById('mm-home-greeting-name');
  if (el) el.textContent = name || 'Panadero';
}

// ─── Settings button ───────────────────────────────────────
function wireSettingsButton(store) {
  document.getElementById('mm-header-settings-btn')?.addEventListener('click', () => {
    renderSettings(store);
    showScreen('settings');
  });
}

// ─── Home buttons ──────────────────────────────────────────
function wireHomeButtons(store) {
  ['p1', 'p2', 'p3'].forEach(type => {
    document.getElementById(`mm-btn-start-${type}`)?.addEventListener('click', () => {
      const proc = store.startProcess(type);
      requestNotificationPermission().then(() => scheduleNotifications(proc));
      openDashboard(proc.id, store);
      toast('✅ ¡Proceso iniciado!');
    });
  });

  document.getElementById('mm-btn-view-history')?.addEventListener('click', () => {
    renderHistory(store);
    showScreen('history');
  });
}

// ─── Dashboard buttons ─────────────────────────────────────
function wireDashboardButtons(store) {
  document.getElementById('mm-btn-back-home')?.addEventListener('click', () => {
    stopCountdown();
    stopPhraseRotation();
    document.getElementById('mm-active-badge')?.classList.add('mm-hidden');
    backToHome(store);
  });

  document.getElementById('mm-btn-complete-process')?.addEventListener('click', () => {
    if (!confirm('¿Marcar el proceso como completado?')) return;
    finishProcess(store, 'completed');
  });
}

function wireAbandonButton(store) {
  document.getElementById('mm-btn-abandon')?.addEventListener('click', () => {
    if (!confirm('¿Abandonar el proceso actual? Se guardará en tu historial.')) return;
    finishProcess(store, 'abandoned');
    toast('🚫 Proceso abandonado y archivado');
  });
}

// ─── Open dashboard ────────────────────────────────────────
function openDashboard(processId, store) {
  store.setViewingId(processId);
  const process = store.getProcess(processId);
  showScreen('dashboard');
  updateActiveBadge(process);
  injectDial(process);
  refreshMood(process, store);
  renderActivities(process, store, (dayNum, totalDays) => {
    onDayComplete(dayNum, totalDays, processId, store);
  });
  renderNotifications(process);
  startCountdown(process, store);
  startPhraseRotation();
}

// ─── Day complete ──────────────────────────────────────────
function onDayComplete(dayNum, totalDays, processId, store) {
  if (dayNum === totalDays) {
    toast('🎉 ¡Último día completado! ¡Increíble trabajo!');
    setTimeout(() => showCompletionScreen(store), 1800);
  } else {
    toast(`✅ ¡Día ${dayNum} completado! Mañana: Día ${dayNum + 1} 🌱`);
    const proc = store.getProcess(processId);
    if (proc) {
      store.load(); // refresh currentDay
      injectDial(proc);
      updateActiveBadge(proc);
      refreshMood(proc, store);
    }
  }
}

// ─── Completion screen ─────────────────────────────────────
function showCompletionScreen(store) {
  stopCountdown();
  stopPhraseRotation();

  const proc   = store.getViewingProcess();
  const type   = proc?.type || 'p1';
  const procId = proc?.id;

  document.querySelectorAll('.mm-screen').forEach(s => s.classList.remove('is-active'));
  const screen = document.getElementById('mm-screen-completion');
  if (screen) screen.classList.add('is-active');

  // Web Share button
  const shareBtn = document.getElementById('mm-btn-share');
  if (shareBtn) {
    shareBtn.classList.remove('mm-hidden');
    shareBtn.onclick = () => shareCompletion(type);
  }

  // Rating stars — re-wire each time (clone to remove old listeners)
  screen?.querySelectorAll('.mm-star').forEach((star, i) => {
    const fresh = star.cloneNode(true);
    star.replaceWith(fresh);
    fresh.addEventListener('click', () => {
      screen.querySelectorAll('.mm-star').forEach((s, j) => {
        s.classList.toggle('is-active', j <= i);
      });
      if (procId) store.archiveProcess(procId, 'completed', i + 1);
      _notifyNewAchievements(store);
      toast('⭐ ¡Gracias por tu valoración!');
      setTimeout(() => backToHome(store), 1200);
    });
  });

  const skipBtn = screen?.querySelector('#mm-btn-skip-rating');
  if (skipBtn) {
    const fresh = skipBtn.cloneNode(true);
    skipBtn.replaceWith(fresh);
    fresh.addEventListener('click', () => {
      if (procId) store.archiveProcess(procId, 'completed', null);
      _notifyNewAchievements(store);
      backToHome(store);
    });
  }
}

function finishProcess(store, outcome) {
  stopCountdown();
  stopPhraseRotation();
  const procId = store.state.viewingId;
  if (procId) {
    clearNotificationsForProcess(procId);
    store.archiveProcess(procId, outcome);
  }
  backToHome(store);
}

function backToHome(store) {
  const state = store.load();
  showScreen('home');
  document.getElementById('mm-active-badge')?.classList.add('mm-hidden');
  renderActiveProcessList(store);
  const firstProc = state.processes?.[0] ?? null;
  refreshMood(firstProc, store);
  if (state.history?.length) {
    document.getElementById('mm-btn-view-history')?.classList.remove('mm-hidden');
  }
}

// ─── Active processes list ──────────────────────────────────
function renderActiveProcessList(store) {
  const processes = store.getActiveProcesses();
  const container = document.getElementById('mm-processes-list');
  if (!container) return;

  if (!processes.length) {
    container.classList.add('mm-hidden');
    container.innerHTML = '';
    return;
  }

  container.classList.remove('mm-hidden');
  container.innerHTML = processes.map(proc => {
    const total = PROCESSES[proc.type]?.totalDays ?? 7;
    const pct   = Math.round(((proc.currentDay - 1) / total) * 100);
    return `
      <div class="mm-process-item">
        <span class="mm-process-emoji" aria-hidden="true">${proc.type === 'p1' ? '🌱' : proc.type === 'p2' ? '🍞' : '🍕'}</span>
        <div class="mm-process-info">
          <div class="mm-process-label">${proc.label}</div>
          <div class="mm-process-day">Día ${proc.currentDay} de ${total}</div>
          <div class="mm-process-bar"><div class="mm-process-bar-fill" style="width:${pct}%"></div></div>
        </div>
        <button class="mm-btn mm-btn--outline mm-btn--sm" data-proc-id="${proc.id}">Continuar →</button>
      </div>
    `;
  }).join('');

  container.querySelectorAll('.mm-process-item button').forEach(btn => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.procId;
      openDashboard(id, store);
    });
  });
}

// ─── Share ─────────────────────────────────────────────────
async function shareCompletion(type) {
  const shareTexts = {
    p1: '¡Completé mi fermentación de masa madre en 7 días con MASAGOTCHI! 🌱🍞 ¡Mi cultivo está vivo!',
    p2: '¡Acabo de hornear mi pan de masa madre artesanal con MASAGOTCHI! 🍞✨ ¡Huele increíble!',
    p3: '¡Acabo de hacer mi pizza de masa madre con MASAGOTCHI! 🍕✨ ¡Borde aireado y base crujiente!',
  };
  const text = shareTexts[type] ?? shareTexts['p2'];

  if (navigator.share) {
    try {
      await navigator.share({ title: 'MASAGOTCHI 🍞', text, url: window.location.href });
    } catch (_) {}
  } else if (navigator.clipboard) {
    await navigator.clipboard.writeText(text);
    toast('📋 ¡Copiado! Compártelo donde quieras');
  }
}

// ─── SVG Dial ──────────────────────────────────────────────
function injectDial(process) {
  const wrapper = document.getElementById('mm-dial-wrapper');
  if (!wrapper) return;
  wrapper.innerHTML = '';
  wrapper.appendChild(createDial(process));
}

// ─── Header badge ──────────────────────────────────────────
function updateActiveBadge(process) {
  const badge = document.getElementById('mm-active-badge');
  const label = document.getElementById('mm-active-badge-label');
  if (!badge || !label) return;
  const total = PROCESSES[process.type]?.totalDays ?? 7;
  label.textContent = `${process.label} · Día ${process.currentDay}/${total}`;
  badge.classList.remove('mm-hidden');
}


// ─── History screen ────────────────────────────────────────
function wireHistoryScreen(store) {
  document.getElementById('mm-btn-back-from-history')?.addEventListener('click', () => {
    showScreen('home');
    renderActiveProcessList(store);
  });
  document.getElementById('mm-btn-history-new-process')?.addEventListener('click', () => {
    showScreen('home');
    renderActiveProcessList(store);
  });
}

// ─── Achievements ──────────────────────────────────────────
function _notifyNewAchievements(store) {
  const newBadges = checkAchievements(store);
  newBadges.forEach((badge, i) => {
    setTimeout(() => {
      toast(`${badge.emoji} ¡Logro desbloqueado! "${badge.title}"`);
    }, i * 1800);
  });
}

// ─── Notification permission ────────────────────────────────
async function requestNotificationPermission() {
  if (Capacitor.isNativePlatform()) {
    try {
      const status = await LocalNotifications.requestPermissions();
      if (status.display === 'granted') {
        toast('🔔 Notificaciones activadas');
      }
    } catch (e) {
      console.warn('[MM] Capacitor push perms error:', e);
    }
    return;
  }

  if (!('Notification' in window)) return;
  if (Notification.permission === 'granted') return;
  if (Notification.permission === 'denied') return;
  try {
    const r = await Notification.requestPermission();
    if (r === 'granted') toast('🔔 Notificaciones activadas');
  } catch (_) {}
}


